import { useState, useEffect } from 'react';
import { Video, Plus, X, Check, Trash2, Upload } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const uploadFile = async (file, category) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API_URL}/upload?category=${category}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data.url;
};

export default function AdminVideos() {
  const [videos, setVideos] = useState([]);
  const [editingVideo, setEditingVideo] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newVideo, setNewVideo] = useState({
    title: '', description: '', video_url: '', video_type: 'embed',
    thumbnail_url: '', duration: 0, price: 0.0, is_exclusive: false
  });

  const fetchVideos = async () => {
    try {
      const res = await axios.get(`${API_URL}/videos`);
      setVideos(res.data);
    } catch {
      toast.error('Failed to load videos');
    }
  };

  useEffect(() => { fetchVideos(); }, []);

  const handleThumbnailUpload = async (e, isEditing = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    setUploading(true);
    try {
      const url = await uploadFile(file, 'image');
      if (isEditing) setEditingVideo(prev => ({ ...prev, thumbnail_url: url }));
      else setNewVideo(prev => ({ ...prev, thumbnail_url: url }));
      toast.success('Thumbnail uploaded!');
    } catch { toast.error('Failed to upload thumbnail'); }
    finally { setUploading(false); }
  };

  const createVideo = async () => {
    if (!newVideo.title || !newVideo.video_url || !newVideo.thumbnail_url) {
      toast.error('Title, video URL, and thumbnail are required');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/videos`, newVideo);
      toast.success('Video added!');
      setShowCreate(false);
      setNewVideo({ title: '', description: '', video_url: '', video_type: 'embed', thumbnail_url: '', duration: 0, price: 0.0, is_exclusive: false });
      fetchVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add video');
    }
  };

  const saveVideo = async (video) => {
    try {
      await axios.put(`${API_URL}/admin/videos/${video.id}`, {
        title: video.title,
        description: video.description,
        video_url: video.video_url,
        video_type: video.video_type,
        thumbnail_url: video.thumbnail_url,
        duration: video.duration,
        price: video.price,
        is_exclusive: video.is_exclusive
      });
      toast.success('Video updated');
      setEditingVideo(null);
      fetchVideos();
    } catch { toast.error('Failed to update video'); }
  };

  const deleteVideo = async (id) => {
    if (!window.confirm('Delete this video? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/admin/videos/${id}`);
      toast.success('Video deleted');
      fetchVideos();
    } catch { toast.error('Failed to delete video'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Manage Videos</h2>
        <button onClick={() => setShowCreate(true)} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" /> Add Video
        </button>
      </div>

      {showCreate && (
        <div className="glass-card p-6 mb-4">
          <h3 className="text-lg font-semibold mb-4">Add New Video</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Title</Label>
              <Input value={newVideo.title} onChange={(e) => setNewVideo({...newVideo, title: e.target.value})} className="input-dark mt-1" />
            </div>
            <div>
              <Label>Video Type</Label>
              <select value={newVideo.video_type} onChange={(e) => setNewVideo({...newVideo, video_type: e.target.value})} className="input-dark mt-1 w-full rounded-md">
                <option value="embed">Embed (YouTube / Vimeo)</option>
                <option value="hosted">Hosted (direct URL)</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <Label>Video URL</Label>
              <Input value={newVideo.video_url} onChange={(e) => setNewVideo({...newVideo, video_url: e.target.value})} className="input-dark mt-1" placeholder="https://youtube.com/embed/..." />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Input value={newVideo.description} onChange={(e) => setNewVideo({...newVideo, description: e.target.value})} className="input-dark mt-1" />
            </div>
            <div>
              <Label>Thumbnail Image</Label>
              <div className="flex gap-2 mt-1">
                <Input value={newVideo.thumbnail_url} onChange={(e) => setNewVideo({...newVideo, thumbnail_url: e.target.value})} className="input-dark flex-1" placeholder="URL or upload" />
                <label className="btn-ghost cursor-pointer flex items-center gap-1 px-3">
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Upload'}
                  <input type="file" className="hidden" accept="image/*" onChange={(e) => handleThumbnailUpload(e, false)} disabled={uploading} />
                </label>
              </div>
              {newVideo.thumbnail_url && <img src={newVideo.thumbnail_url} alt="Thumbnail" className="w-20 h-12 rounded object-cover mt-2" />}
            </div>
            <div>
              <Label>Duration (seconds)</Label>
              <Input type="number" value={newVideo.duration} onChange={(e) => setNewVideo({...newVideo, duration: parseInt(e.target.value) || 0})} className="input-dark mt-1" />
            </div>
            <div>
              <Label>Price ($) — 0 for free</Label>
              <Input type="number" step="0.01" value={newVideo.price} onChange={(e) => setNewVideo({...newVideo, price: parseFloat(e.target.value) || 0})} className="input-dark mt-1" />
            </div>
            <div className="flex items-center gap-2 mt-6">
              <input type="checkbox" id="new-exclusive" checked={newVideo.is_exclusive} onChange={(e) => setNewVideo({...newVideo, is_exclusive: e.target.checked})} />
              <Label htmlFor="new-exclusive">Premium exclusive</Label>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setShowCreate(false)} className="btn-ghost">Cancel</button>
            <button onClick={createVideo} className="btn-primary"><Plus className="w-4 h-4 mr-2" /> Add Video</button>
          </div>
        </div>
      )}

      {videos.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <Video className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Videos Yet</h3>
          <p className="text-gray-400 mb-4">Add your first video to get started</p>
        </div>
      ) : (
        videos.map(video => (
          <div key={video.id} className="glass-card p-4">
            {editingVideo?.id === video.id ? (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div><Label>Title</Label><Input value={editingVideo.title} onChange={(e) => setEditingVideo({...editingVideo, title: e.target.value})} className="input-dark mt-1" /></div>
                  <div>
                    <Label>Video Type</Label>
                    <select value={editingVideo.video_type} onChange={(e) => setEditingVideo({...editingVideo, video_type: e.target.value})} className="input-dark mt-1 w-full rounded-md">
                      <option value="embed">Embed</option>
                      <option value="hosted">Hosted</option>
                    </select>
                  </div>
                  <div className="md:col-span-2"><Label>Video URL</Label><Input value={editingVideo.video_url} onChange={(e) => setEditingVideo({...editingVideo, video_url: e.target.value})} className="input-dark mt-1" /></div>
                  <div className="md:col-span-2"><Label>Description</Label><Input value={editingVideo.description} onChange={(e) => setEditingVideo({...editingVideo, description: e.target.value})} className="input-dark mt-1" /></div>
                  <div>
                    <Label>Thumbnail</Label>
                    <div className="flex gap-2 mt-1">
                      <Input value={editingVideo.thumbnail_url} onChange={(e) => setEditingVideo({...editingVideo, thumbnail_url: e.target.value})} className="input-dark flex-1" />
                      <label className="btn-ghost cursor-pointer flex items-center gap-1 px-3">
                        <Upload className="w-4 h-4" />{uploading ? '...' : 'Upload'}
                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleThumbnailUpload(e, true)} disabled={uploading} />
                      </label>
                    </div>
                    {editingVideo.thumbnail_url && <img src={editingVideo.thumbnail_url} alt="Thumbnail" className="w-20 h-12 rounded object-cover mt-1" />}
                  </div>
                  <div><Label>Duration (seconds)</Label><Input type="number" value={editingVideo.duration} onChange={(e) => setEditingVideo({...editingVideo, duration: parseInt(e.target.value) || 0})} className="input-dark mt-1" /></div>
                  <div><Label>Price ($)</Label><Input type="number" step="0.01" value={editingVideo.price} onChange={(e) => setEditingVideo({...editingVideo, price: parseFloat(e.target.value) || 0})} className="input-dark mt-1" /></div>
                  <div className="flex items-center gap-2 mt-6">
                    <input type="checkbox" checked={editingVideo.is_exclusive} onChange={(e) => setEditingVideo({...editingVideo, is_exclusive: e.target.checked})} />
                    <Label>Premium exclusive</Label>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditingVideo(null)} className="btn-ghost"><X className="w-4 h-4" /></button>
                  <button onClick={() => saveVideo(editingVideo)} className="btn-primary"><Check className="w-4 h-4" /></button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <img src={video.thumbnail_url || '/placeholder-album.png'} alt={video.title} className="w-20 h-12 rounded-lg object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold truncate">{video.title}</h3>
                    {video.is_exclusive && <span className="px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-400">Premium</span>}
                    {video.price > 0 && <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400">${video.price.toFixed(2)}</span>}
                  </div>
                  <p className="text-sm text-gray-400 truncate">{video.description}</p>
                  <p className="text-xs text-gray-500">{video.views || 0} views · {video.video_type}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => setEditingVideo({...video})} className="btn-ghost">Edit</button>
                  <button onClick={() => deleteVideo(video.id)} className="btn-ghost text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
